import React, { useMemo, useContext, createContext, useState } from 'react'

import isPropValid from '@emotion/is-prop-valid'
import { merge } from 'lodash'
import PropTypes from 'prop-types'
import {
  StyleSheetManager,
  ThemeProvider as DefaultThemeProvider,
} from 'styled-components'

import { useLanguage } from '@/utils/localization/LanguageContext'

import { DisplayProvider } from './DisplayProvider'
import GlobalStyle from './GlobalStyle'
import primaryTheme from './primaryTheme'

export const ThemeContext = createContext()
export const useThemeControl = () => useContext(ThemeContext)

function shouldForwardProp(propName, target) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

export default function ThemeProvider({ children, ...props }) {
  const { isRtl } = useLanguage()
  const [colors, setColors] = useState(null)
  const primaryThemeMemo = useMemo(() => {
    const combinedColors = colors
      ? merge(primaryTheme.colors, colors)
      : primaryTheme.colors
    return {
      ...primaryTheme,
      colors: combinedColors,
      isRtl,
    }
  }, [isRtl, colors])

  const value = {
    colors,
    setColors,
  }

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ThemeContext.Provider value={value}>
        <DefaultThemeProvider theme={primaryThemeMemo} {...props}>
          <GlobalStyle />
          <DisplayProvider>{children}</DisplayProvider>
        </DefaultThemeProvider>
      </ThemeContext.Provider>
    </StyleSheetManager>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.any,
}
